<template>
    <div    class="">
        <tailwind-table
                            :data="$data._data"
                            :headers="$data._headers"
                            :exportable="exportable"
                            :export-csv-header="csvHeaders"
                            :export-fetch-route="exportFetchRoute"
                            :export-fetch-parameters="exportFetchParameters"
                            :sortBy="$data._sortBy"
                            :sortable="true"
                            :sortDesc="$data._sortDesc"
                            :no-data-message="noDataMessage"
                            v-model:selected-row="$data._selectedRow"
                            :allow-highlighted-rows="$data._allowHighlightedRows"
                            :row-selectable="rowSelectable"
                            :use-group-table-styling="useGroupTableStyling"
                            @rowSelected="rowAction"
                            @update:sort-by="updateSort"
        >
            <template   #above_table_left>
                <div v-if="searchEnabled" class="mr-0 sm:mr-4 mb-4 flex flex-col md:flex-row items-center">
                    <!-- Search Slot -->
                    <slot   name="search">
                        <div class="flex flex-col justify-around w-full">
                            <div class="flex flex-col justify-between md:flex-row md:items-center">
                                <div class="w-full">
                                    <floating-input
                                        label="Search"
                                        id="search"
                                        v-model:value="$data._search"
                                        @change="updateSearch($event.target.value)"
                                    ></floating-input>
                                </div>
                            </div>
                        </div>
                    </slot>

                    <!-- Additional Items -->
                    <slot   name="search-right">
                    </slot>

                </div>


            </template>

            <template #above_table_center>
                <slot name="above_table_center"></slot>
            </template>

            <template   #above_table_right>
                <div v-if="itemsPerPageEnabled"   class="flex justify-between">
                    <!-- Area often left open to the left of Items Per Page -->
                    <slot   name="left-of-items-per-page">

                    </slot>

                    <!-- Items Per Page -->
                    <slot   name="items-per-page">
                        <div    class="flex flex-row justify-start md:justify-end"
                                style="min-width: 15rem;">
                            <items-per-page-select  v-model:items-per-page="$data._itemsPerPage"
                                                    :current-high="$data._pageInfo.to"
                                                    :current-low="$data._pageInfo.from"
                                                    :total-results="$data._pageInfo.total"
                                                    @update:itemsPerPage="updateItemsPerPage"
                            ></items-per-page-select>
                        </div>
                    </slot>
                </div>
            </template>

            <!-- Group Row -->
            <template   #group-row="props">
                <slot   name="group-row"
                        :row="props.row"
                        :rowIndex="props.rowIndex"></slot>
            </template>

            <!-- Column 1 -->
            <template   #col-1="props">
                <slot   name="col-1"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- Column 2 -->
            <template   #col-2="props">
                <slot   name="col-2"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- Column 3 -->
            <template   #col-3="props">
                <slot   name="col-3"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- Column 4 -->
            <template   #col-4="props">
                <slot   name="col-4"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- Column 5 -->
            <template   #col-5="props">
                <slot   name="col-5"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- Column 6 -->
            <template   #col-6="props">
                <slot   name="col-6"
                        :row="props.row"
                        :rowIndex="props.rowIndex"
                >
                </slot>
            </template>

            <!-- No Data Entry -->
            <template   #no-data>
                <div    v-if="$data._loading">
                    <div class="flex flex-row items-center justify-center py-4">
                        <span :class="iconLoading"></span>
                        <span class="text-grey-500 text-base font-medium">{{ loadingMessage }}</span>
                    </div>
                </div>
            </template>
        </tailwind-table>

        <slot   name="pagination">
            <pagination     v-if="!($data._loading)"
                            :num-pages="$data._pageInfo.pages"
                            v-model:page="$data._page"
                            @update:page="updatePage($event)"
            ></pagination>
        </slot>
    </div>
</template>

<script>

import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import Pagination from "@/components/Pagination";
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect";
import FloatingInput from "@/components/elements/FloatingInput";
import GreenButton from "@/components/elements/green-button";
import TailwindTable from "@/components/elements/TailwindTable";

const LOAD_DATA_DEBOUNCE = 250

export default {
    name: "AjaxTable",
    mixins: [iconNamesMixin],
    emits: ['loadData', 'rowSelected', 'updatingItemsPerPage', 'update:selectedRow', 'update:allowHighlightedRows',
            'update:page', 'update:sortBy', 'update:sortDesc', 'update:search', 'update:itemsPerPage', 'update:loading'],
    components: {TailwindTable, GreenButton, Pagination, ItemsPerPageSelect, FloatingInput,},
    props: {
        // Data
        data: Array,

        // An Array of Objects containing the following entries: {name, key, [sortBy], [show]}
        headers: Array,

        // An object of Pretty Header => key for the csv.
        csvHeaders: {
            type: Object,
            default: () => {
                return null
            }
        },

        // For Data Export
        exportable: {
            type: Boolean,
            default: true,
        },
        exportFetchRoute: {
            type: String,
            default: null,
        },
        exportFetchParameters: {
            type: Object,
            default: () => {
                return {}
            },
        },

        // For Sorting
        sortBy: {
            type: String,
            default: '',
        },
        sortDesc: {
            type: Boolean,
            default: false,
        },

        // For Paging
        page: {
            type: Number,
            default: 1
        },
        itemsPerPage: {
            type: String,
            default: '100',
        },
        itemsPerPageEnabled: {
            type: Boolean,
            default: true,
        },

        // Simply returned by Eloquent's paginate method
        pageInfo: {
            type: Object,
            default: () => {
                return {
                    pages: 1,
                    from: 1,
                    to: 1,
                    total: 1
                }
            }
        },
        searchEnabled: {
          type: Boolean,
          default: true
        },
        search: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        noDataMessage: {
            type: String,
            default: 'No Data Found',
        },
        loadingMessage: {
            type: String,
            default: 'Loading',
        },
        rowSelectable: {
            type: Boolean,
            default: true,
        },
        selectedRow: {
            type: [Number, null],
            default: null,
        },
        allowHighlightedRows: {
            type: Boolean,
            default: false,
        },
        useGroupTableStyling: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loadingFunction: null,
            _selectedRow: this.selectedRow,
            _allowHighlightedRows: this.allowHighlightedRows,

            // Basic Table Structure
            _search: this.search,
            _sortBy: this.sortBy,
            _sortDesc: this.sortDesc,
            _page: this.page,
            _itemsPerPage: this.itemsPerPage,
            _pageInfo: this.pageInfo,
            _loading: this.loading,

            // Data for Table
            _data: this.data,
            _headers: this.headers,
        }
    },
    created() {
        this.loadingFunction = _.debounce(() => {
            this.$emit('loadData')
        }, LOAD_DATA_DEBOUNCE)
    },
    mounted() {
        this.loadData()
    },
    methods: {
        rowAction(rowIndex) {
            if (this.rowSelectable) {
                this.$emit('rowSelected', rowIndex)
            }
        },
        loadData() {
            this.setLoading(true)
            this.$data._data = []
            this.$data._data.length = 0
            this.loadingFunction()
        },
        setLoading(loading) {
            this.$data._loading = loading
            this.$emit('update:loading', this.$data._loading)
        },
        updateSearch(search) {
            this.$data._search = search
            this.$emit('update:search', this.$data._search)
            this.loadData()
        },
        updatePage(page) {
            this.$data._page = page
            this.$emit('update:page', this.$data._page)
            this.loadData()
        },
        updateSort(by) {
            if (this.$data._sortBy === by) {
                this.$data._sortBy = this.$data._sortDesc ? '' : this.$data._sortBy
                this.$data._sortDesc = !this.$data._sortDesc
            }
            else {
                this.$data._sortBy = by
                this.$data._sortDesc = false
            }
            this.$emit('update:sortBy', this.$data._sortBy)
            this.$emit('update:sortDesc', this.$data._sortDesc)

            this.loadData()
        },
        updateItemsPerPage(itemsPerPage) {
          console.log('updatingItemsPerPage')
            this.$data._itemsPerPage = itemsPerPage
            this.$emit('update:itemsPerPage', this.$data._itemsPerPage)
            this.loadData()
        },
    },
    computed: {
    },
    watch: {
        loading() {
            this.$data._loading = this.loading
            if (this.loading) {
                this.$data._data = []
            }
        },
        data: {
          handler(newData, oldData){
            this.$data._data = this.data
          },
          deep: true
        },
        page() {
            this.$data._page = this.page
            this.loadData()
        },
        sortBy() {
            this.$data._sortBy = this.sortBy
            this.loadData()
        },
        sortDesc() {
            this.$data._sortDesc = this.sortDesc
            this.loadData()
        },
        search() {
            this.$data._search = this.search
            this.loadData()
        },
        _search(){
          this.updateSearch(this._search)
        },
        itemsPerPage() {
            this.$data._itemsPerPage = this.itemsPerPage
            this.loadData()
        },
        pageInfo() {
            this.$data._pageInfo = this.pageInfo
        },
        _selectedRow(val, old) {
            if (val !== old) {
                this.$emit('update:selectedRow', val)
            }
        },
        selectedRow(val, old) {
            if (val !== old) {
                this.$data._selectedRow = val
            }
        },
        _allowHighlightedRows(val, old) {
            if (val !== old) {
                this.$emit('update:allowHighlightedRows', val)
            }
        },
        allowHighlightedRows(val, old) {
            if (val !== old) {
                this.$data._allowHighlightedRows = val
            }
        },
    },
}

</script>

<style scoped>

</style>